import axios from "axios";
import { useLocalization } from "../../../i18n/LocalizationProvider";
import MenuText from "../../../i18n/MenuText";
import React, { useState, useEffect, useContext, useCallback } from "react";
import PosItemCard from "./PosItemCard";
import { Box, Chip, Grid, ListItemButton, Typography } from "@mui/material";
import { SortableContainer, SortableElement, arrayMove } from 'react-sortable-hoc';
import _ from "lodash";
import { AppContext } from "../../../AppContext";
import { useTranslation } from "react-i18next";
import { useIndependentUserSortSettings } from "../../../shared/hooks/useIndependentUserSortSettings";
import {
    buildSortOrder,
    getItemSortSettingsDoctype,
} from "../../../utils/menuSettingsSortUtils";
import OtherCompanyStockChips from "./OtherCompanyStockChips";
import {
    formatStockQuantity,
    getRetailAvailableQuantity,
    getRetailDraftSalesQuantity,
} from "../../../shared/utils/companyStockUtils";

// SortableItem component
const SortableItem = SortableElement(({ item, index, onClick, isDraggable }) => (
    <Grid item xs={6}>
        <PosItemCard item={item} index={index} onClick={onClick} isDragDropMode={isDraggable}/>
    </Grid>
));

// SortableList component
const SortableList = SortableContainer(({ items, onClick, isDraggable }) => (
    <Grid 
        container 
        spacing={3}
        style={{ 
            paddingBottom: '40px' // Increased bottom padding for consistency
        }}
    >
        {items.map((item, index) => (
            <SortableItem key={`item-${item.item_code}`} index={index} item={item} onClick={onClick} isDraggable={isDraggable}/>
        ))}
    </Grid>
));

const BidiText = ({ children, variant, sx, ...props }) => (
    <Typography
        variant={variant}
        dir="auto"
        sx={{
            unicodeBidi: "plaintext",
            textAlign: "start",
            ...sx,
        }}
        {...props}
    >
        {children}
    </Typography>
);

export default function PosItemList({
    onClick,
    menuItems,
    groupedItems,
    subGroups,
    category,
    subCategory,
    onItemsLoaded,
    isDragDropMode,
    searchText,
    barcodeItemCode,
    viewMode = "grid",
    isRetailPos = false,
}) {
    const { t } = useTranslation();
    const { menuText } = useLocalization();
    const { userMenuSettings, menuRootGroupId, posProfile, socket } = useContext(AppContext);
    const [filteredItems, setFilteredItems] = useState([]);
    const [promotionItems,setPromotionItems] = useState({});
    useEffect(()=>{
        if(!isRetailPos || !posProfile?.name){setPromotionItems({});return;}
        let active=true;
        const load=async()=>{try{const {data}=await axios.get("method/restaurant_management.retail_promotions.catalogue",{params:{pos_profile:posProfile.name}});const map={};(data.message||[]).forEach(p=>p.items.forEach(r=>{map[r.item_code]=map[r.item_code] ? "Multiple offers" : p.offer_type==="Discount"?`${r.use_override?r.discount_percentage:p.discount_percentage}% offer`:`Buy ${p.buy_qty} + ${p.free_qty} free`;}));if(active)setPromotionItems(map);}catch{if(active)setPromotionItems({});}};
        load();const timer=setInterval(load,30000);socket?.on?.("retail_promotions_changed",load);
        return()=>{active=false;clearInterval(timer);socket?.off?.("retail_promotions_changed",load);};
    },[isRetailPos,posProfile?.name,socket]);

    const rootGroupId = menuRootGroupId || "All Item Groups";
    const categoryId =
        category?.id === "All Item Groups" ? rootGroupId : category?.id;
    const itemSortGroupId = subCategory?.id || categoryId || rootGroupId;
    const itemSortSettingsDoctype = getItemSortSettingsDoctype(itemSortGroupId);
    const { savedOrder: itemOrder, saveOrder: saveItemOrder } =
        useIndependentUserSortSettings({
            doctype: itemSortSettingsDoctype,
            orderKey: "item_order",
            legacyIndices: userMenuSettings?.item_sort_indices,
            legacyEntryKey: "item_code",
        });
    const getQtyValue = (item) => getRetailAvailableQuantity(item);
    const getPriceValue = (item) => {
        const values = [item.standard_rate, item.price_list_rate, item.rate]
            .map((value) => Number(value))
            .filter((value) => Number.isFinite(value));
        return values.find((value) => value > 0) ?? values[0] ?? 0;
    };
    const getPriceLabel = (item) => {
        const currency = item.currency ? ` ${item.currency}` : "";
        return `${getPriceValue(item).toFixed(2)}${currency}`;
    };
    const getAttributeValue = (item, attributeNames) => {
        const names = Array.isArray(attributeNames)
            ? attributeNames
            : [attributeNames];
        const normalizedNames = names.map((name) =>
            String(name || "").trim().toLowerCase()
        );
        const directValue = names
            .map((name) => item?.[String(name || "").toLowerCase()])
            .find(Boolean);
        if (directValue) return directValue;

        const attributes = item?.variant_attributes || item?.attributes || [];
        if (!Array.isArray(attributes)) return "";

        const match = attributes.find((entry) => {
            const label = String(entry?.attribute || entry?.attribute_name || "")
                .trim()
                .toLowerCase();
            return normalizedNames.includes(label);
        });
        return match?.attribute_value || match?.value || "";
    };

    const getQtyLabel = (item) => {
        const qty = getQtyValue(item);
        if (qty === undefined || qty === null || qty === "") return "-";
        return formatStockQuantity(qty);
    };
    const getColorCode = (colorName) => {
        if (!colorName) return "#6c757d";
        const colorLower = String(colorName).trim().toLowerCase();
        const colorMap = {
            red: "#ff5c5c",
            blue: "#4d79f6",
            green: "#38cc8c",
            yellow: "#ffde59",
            black: "#343a40",
            white: "#f8f9fa",
            gray: "#6c757d",
            grey: "#6c757d",
            orange: "#fd7e14",
            purple: "#6f42c1",
            pink: "#e83e8c",
            brown: "#795548",
            navy: "#001f3f",
            teal: "#20c997",
            cyan: "#17a2b8",
            lime: "#32cd32",
            maroon: "#800000",
            olive: "#808000",
            silver: "#c0c0c0",
            gold: "#ffd700",
        };
        if (colorMap[colorLower]) return colorMap[colorLower];
        const partialMatch = Object.keys(colorMap).find((key) =>
            colorLower.includes(key)
        );
        return partialMatch ? colorMap[partialMatch] : "#6c757d";
    };
    const isColorAttribute = (attributeName) => {
        const value = String(attributeName || "").toLowerCase();
        return value.includes("color") || value.includes("colour");
    };
    const isSizeAttribute = (attributeName) => {
        const value = String(attributeName || "").toLowerCase();
        return value.includes("size") || value.includes("مقاس");
    };
    const formatAttributeValue = (value) => {
        const numericValue = Number(value);
        if (!Number.isFinite(numericValue)) return value;
        return numericValue.toFixed(1).endsWith(".0")
            ? numericValue.toString()
            : numericValue.toFixed(1);
    };
    const getItemAttributes = (item) => {
        const attributes = item?.variant_attributes || item?.attributes || [];
        if (!Array.isArray(attributes)) return [];
        return attributes
            .map((attribute) => ({
                attribute: attribute?.attribute || attribute?.attribute_name || "",
                value: attribute?.attribute_value || attribute?.value || "",
            }))
            .filter((attribute) => attribute.attribute && attribute.value);
    };
    const getInitials = (value) => {
        const text = String(value || "").trim();
        if (!text) return "?";
        return text
            .split(/\s+/)
            .slice(0, 2)
            .map((part) => part[0])
            .join("")
            .toUpperCase();
    };

    const getVariantGroupKey = (item) => item.variant_of || item.item_code;

    const getVariantGroups = () => {
        const groups = _.groupBy(filteredItems || [], getVariantGroupKey);
        return Object.entries(groups).map(([key, items]) => {
            const parentItem =
                items.find((item) => item.item_code === key) ||
                items.find((item) => item.parent_item)?.parent_item ||
                items[0] ||
                {};
            const hasVariantRows = items.some((item) => item.variant_of);
            const variantRows = hasVariantRows
                ? items.filter((item) => item.item_code !== key)
                : items;
            const variants = [...variantRows].sort((a, b) => {
                const colorA = String(getAttributeValue(a, ["Color", "Colour"]));
                const colorB = String(getAttributeValue(b, ["Color", "Colour"]));
                const sizeA = String(getAttributeValue(a, "Size"));
                const sizeB = String(getAttributeValue(b, "Size"));
                return (
                    colorA.localeCompare(colorB) ||
                    sizeA.localeCompare(sizeB) ||
                    String(a.item_name || a.item_code).localeCompare(
                        String(b.item_name || b.item_code)
                    )
                );
            });
            const totalQty = variants.reduce((sum, item) => {
                const qty = Number(getQtyValue(item));
                return sum + (Number.isFinite(qty) ? qty : 0);
            }, 0);
            const prices = variants.map(getPriceValue).filter(Number.isFinite);
            const minPrice = prices.length ? Math.min(...prices) : 0;
            const maxPrice = prices.length ? Math.max(...prices) : 0;
            const currency = variants.find((item) => item.currency)?.currency || "";
            const priceLabel =
                minPrice === maxPrice
                    ? `${minPrice.toFixed(2)}${currency ? ` ${currency}` : ""}`
                    : `${minPrice.toFixed(2)} - ${maxPrice.toFixed(2)}${
                          currency ? ` ${currency}` : ""
                      }`;

            return {
                key,
                parentItem,
                variants,
                variantsCount: variants.length,
                totalQty,
                priceLabel,
            };
        });
    };

    const applySavedSort = useCallback((items) => {
        if (itemOrder.length > 0) {
            const itemIndexMap = itemOrder.reduce((acc, itemCode, index) => {
                acc[itemCode] = index;
                return acc;
            }, {});

            return [...items].sort((a, b) => {
                const indexA = itemIndexMap[a.item_code] || 0;
                const indexB = itemIndexMap[b.item_code] || 0;
                return indexA - indexB;
            });
        }

        return items || [];
    }, [itemOrder]);

    const getBarcodeFamilyItems = useCallback((items) => {
        if (!barcodeItemCode) return null;
        const matchedItem = _.find(items, { item_code: barcodeItemCode });
        if (!matchedItem) return null;

        const parentCode = matchedItem.variant_of || matchedItem.item_code;
        return _.filter(items, (item) => {
            return item.item_code === parentCode || item.variant_of === parentCode;
        });
    }, [barcodeItemCode]);

    useEffect(() => {
        let filteredItems = menuItems || [];

        const query = (searchText || "").trim().toLowerCase();

        const barcodeFamilyItems = isRetailPos
            ? getBarcodeFamilyItems(menuItems || [])
            : null;

        if (barcodeFamilyItems) {
            filteredItems = barcodeFamilyItems;
        } else if (query.length > 0) {
            if (isRetailPos) {
                // Retail searches are already filtered by ERPNext's POS endpoint.
                // Trust those results so barcode, serial/batch, and configured POS
                // search-field matches are not discarded by narrower client logic.
                filteredItems = menuItems || [];
            } else {
                filteredItems = _.filter(menuItems, (item) => {
                    const code = String(item.item_code || "").toLowerCase();
                    const name = String(menuText(item.item_name || "")).toLowerCase();
                    const group = String(menuText(item.item_group || "")).toLowerCase();
                    const barcodeValues = Array.isArray(item.barcodes)
                        ? item.barcodes
                              .map((entry) => entry?.barcode)
                              .filter(Boolean)
                              .join(" ")
                              .toLowerCase()
                        : "";

                    return (
                        code.includes(query) ||
                        name.includes(query) ||
                        group.includes(query) ||
                        barcodeValues.includes(query)
                    );
                });
            }
        } else {
            const categoryId =
                category?.id === "All Item Groups" ? rootGroupId : category?.id;

            if (categoryId && categoryId !== rootGroupId) {
                let categories = [categoryId];
                const childGroups =
                    (subGroups && subGroups.length > 0
                        ? subGroups
                        : _.find(
                              groupedItems,
                              (groups, parentGroup) =>
                                  parentGroup === categoryId
                          )) || [];

                if (childGroups && childGroups.length > 0) {
                    childGroups.forEach((element) => {
                        categories.push(element.id);
                    });
                }

                filteredItems = _.filter(menuItems, (item) =>
                    _.includes(categories, item.item_group)
                );
            }

            if (subCategory) {
                filteredItems = _.filter(filteredItems, {
                    item_group: subCategory.id,
                });
            }
        }

        filteredItems = applySavedSort(filteredItems);

        setFilteredItems(filteredItems);
    }, [menuText, category, subCategory, groupedItems, menuItems, searchText, subGroups, menuRootGroupId, rootGroupId, isRetailPos, applySavedSort, getBarcodeFamilyItems]);

    useEffect(() => {
        if (onItemsLoaded) {
            onItemsLoaded(menuItems);
        }
    }, [menuItems, onItemsLoaded]);

    const onSortEnd = ({ oldIndex, newIndex }) => {
        const updatedItems = arrayMove(filteredItems, oldIndex, newIndex);
        setFilteredItems(updatedItems);

        // Save the updated order to your state or backend
        if (onItemsLoaded) {
            onItemsLoaded(updatedItems);
        }

        handleSaveFilters(updatedItems);
    };

    const handleSaveFilters = (reorderedItems) => {
        saveItemOrder(buildSortOrder(reorderedItems, "item_code"));
    };

    const renderListView = () => (
        <Box sx={{ width: "100%", paddingBottom: "40px" }}>
            {getVariantGroups().map((group) => (
                <Box
                    key={group.key}
                    sx={{
                        mb: 1.5,
                        border: "1px solid rgba(0, 0, 0, 0.08)",
                        borderRadius: "12px",
                        background: "rgba(255, 255, 255, 0.9)",
                        overflow: "hidden",
                        boxShadow: "0 4px 16px rgba(39, 29, 28, 0.04)",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 1,
                            px: 1.5,
                            py: 1,
                            background: "rgba(225, 73, 84, 0.06)",
                            borderBottom: "1px solid rgba(0, 0, 0, 0.06)",
                        }}
                    >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
                            {group.parentItem.cover || group.parentItem.image ? (
                                <Box
                                    component="img"
                                    src={group.parentItem.cover || group.parentItem.image}
                                    alt={group.parentItem.item_name || group.key}
                                    sx={{
                                        width: 38,
                                        height: 38,
                                        borderRadius: "7px",
                                        objectFit: "cover",
                                        flexShrink: 0,
                                    }}
                                />
                            ) : (
                                <Box
                                    sx={{
                                        width: 38,
                                        height: 38,
                                        borderRadius: "7px",
                                        backgroundColor: "#4d79f6",
                                        color: "#fff",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        fontWeight: 800,
                                        flexShrink: 0,
                                    }}
                                >
                                    {getInitials(group.parentItem.item_name || group.key)}
                                </Box>
                            )}
                            <Box sx={{ minWidth: 0 }}>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        display: "block",
                                        fontWeight: 800,
                                        color: "#271D1C",
                                        textTransform: "uppercase",
                                        mb: 0.25,
                                    }}
                                >
                                    {t("retailPos.parentItem")}
                                </Typography>
                                <BidiText sx={{ fontWeight: 800 }} noWrap>
                                    {group.parentItem.item_code || group.key}
                                </BidiText>
                                {group.parentItem.item_name &&
                                group.parentItem.item_name !== group.parentItem.item_code ? (
                                    <BidiText variant="caption" color="text.secondary" noWrap>
                                        {<MenuText>{group.parentItem.item_name}</MenuText>}
                                    </BidiText>
                                ) : null}
                            </Box>
                        </Box>
                        <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", justifyContent: "flex-end" }}>
                            <Chip
                                size="small"
                                label={`${group.variantsCount} ${t("retailPos.relatedVariants")}`}
                                variant="outlined"
                            />
                            <Chip
                                size="small"
                                label={`${t("retailPos.totalQty")}: ${group.totalQty}`}
                                variant="outlined"
                                sx={{
                                    backgroundColor:
                                        group.totalQty <= 0
                                            ? "rgba(211, 47, 47, 0.08)"
                                            : "rgba(46, 125, 50, 0.08)",
                                    color: group.totalQty <= 0 ? "#d32f2f" : "#2e7d32",
                                    borderColor:
                                        group.totalQty <= 0
                                            ? "rgba(211, 47, 47, 0.22)"
                                            : "rgba(46, 125, 50, 0.22)",
                                }}
                            />
                            <Chip
                                size="small"
                                label={group.priceLabel}
                                color="primary"
                                sx={{ fontWeight: 700 }}
                            />
                        </Box>
                    </Box>
                    {group.variants.map((entry) => {
                        const attributes = getItemAttributes(entry);
                        const colorAttribute = attributes.find((attribute) =>
                            isColorAttribute(attribute.attribute)
                        );
                        const retailDraftSalesQty =
                            getRetailDraftSalesQuantity(entry);
                        return (
                            <ListItemButton
                                key={entry.item_code}
                                onClick={() => onClick?.(entry)}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: 1.5,
                                    px: 1.5,
                                    py: 1.25,
                                    minHeight: 86,
                                    borderBottom: "1px solid rgba(0, 0, 0, 0.04)",
                                    "&:last-child": {
                                        borderBottom: "none",
                                    },
                                }}
                            >
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
                                    {entry.cover ? (
                                        <Box
                                            component="img"
                                            src={entry.cover}
                                            alt={entry.item_name || entry.item_code}
                                            sx={{
                                                width: 48,
                                                height: 48,
                                                borderRadius: "8px",
                                                objectFit: "cover",
                                                flexShrink: 0,
                                            }}
                                        />
                                    ) : (
                                        <Box
                                            sx={{
                                                width: 48,
                                                height: 48,
                                                borderRadius: "50%",
                                                flexShrink: 0,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                backgroundColor: colorAttribute
                                                    ? getColorCode(colorAttribute.value)
                                                    : "#4d79f6",
                                                color:
                                                    colorAttribute &&
                                                    String(colorAttribute.value)
                                                        .toLowerCase()
                                                        .match(/white|light/)
                                                        ? "#000"
                                                        : "#fff",
                                                fontWeight: 800,
                                            }}
                                        >
                                            {getInitials(entry.item_name || entry.item_code)}
                                        </Box>
                                    )}
                                    <Box sx={{ minWidth: 0 }}>
                                        <BidiText sx={{ fontWeight: 800 }} noWrap>
                                            {entry.item_code}
                                        </BidiText>
                                        {entry.item_name && entry.item_name !== entry.item_code ? (
                                            <BidiText
                                                variant="caption"
                                                color="text.secondary"
                                                noWrap
                                                sx={{ display: "block", maxWidth: 260 }}
                                            >
                                                {<MenuText>{entry.item_name}</MenuText>}
                                                {promotionItems[entry.item_code] && <Chip size="small" color="success" label={promotionItems[entry.item_code]} />}
                                            </BidiText>
                                        ) : null}
                                        <Box
                                            sx={{
                                                mt: 0.75,
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 0.75,
                                                flexWrap: "wrap",
                                            }}
                                        >
                                            <Chip
                                                size="small"
                                                label={`${getQtyLabel(entry)} ${t("retailPos.qty")}`}
                                                sx={{
                                                    height: 24,
                                                    fontWeight: 800,
                                                    backgroundColor: "rgba(0, 0, 0, 0.04)",
                                                }}
                                            />
                                            <Chip
                                                size="small"
                                                label={t("retailPos.draftSalesQty", {
                                                    qty: formatStockQuantity(
                                                        retailDraftSalesQty
                                                    ),
                                                })}
                                                sx={{
                                                    height: 24,
                                                    fontWeight: 700,
                                                    backgroundColor:
                                                        retailDraftSalesQty > 0
                                                            ? "rgba(237, 108, 2, 0.1)"
                                                            : "rgba(108, 117, 125, 0.08)",
                                                    color:
                                                        retailDraftSalesQty > 0
                                                            ? "#ed6c02"
                                                            : "#6c757d",
                                                    border: `1px solid ${
                                                        retailDraftSalesQty > 0
                                                            ? "rgba(237, 108, 2, 0.24)"
                                                            : "rgba(108, 117, 125, 0.18)"
                                                    }`,
                                                }}
                                            />
                                            <OtherCompanyStockChips
                                                compact
                                                companyActualQty={
                                                    entry.company_actual_qty
                                                }
                                                currentCompany={
                                                    entry.stock_company
                                                }
                                            />
                                        </Box>
                                    </Box>
                                </Box>

                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "flex-end",
                                        gap: 1,
                                        minWidth: "42%",
                                        flexWrap: "wrap",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "flex-end",
                                            gap: 0.5,
                                            flexWrap: "wrap",
                                            minHeight: 28,
                                        }}
                                    >
                                        {attributes.length > 0 ? (
                                            attributes.map((attribute) => {
                                                const isColor = isColorAttribute(attribute.attribute);
                                                const isSize = isSizeAttribute(attribute.attribute);
                                                const label = formatAttributeValue(attribute.value);
                                                return (
                                                    <Chip
                                                        key={`${entry.item_code}-${attribute.attribute}-${attribute.value}`}
                                                        size="small"
                                                        label={label}
                                                        title={`${attribute.attribute}: ${attribute.value}`}
                                                        sx={{
                                                            height: 24,
                                                            borderRadius: "999px",
                                                            fontWeight: 700,
                                                            ...(isColor
                                                                ? {
                                                                      backgroundColor: getColorCode(attribute.value),
                                                                      color: String(attribute.value)
                                                                          .toLowerCase()
                                                                          .match(/white|light/)
                                                                          ? "#000"
                                                                          : "#fff",
                                                                  }
                                                                : isSize
                                                                ? {
                                                                      backgroundColor: "#6E7187",
                                                                      color: "#fff",
                                                                  }
                                                                : {
                                                                      backgroundColor: "rgba(2, 136, 209, 0.12)",
                                                                      color: "#0277bd",
                                                                  }),
                                                        }}
                                                    />
                                                );
                                            })
                                        ) : (
                                            <Chip
                                                size="small"
                                                label={t("retailPos.noAttribute")}
                                                variant="outlined"
                                            />
                                        )}
                                    </Box>
                                    <Chip
                                        size="small"
                                        label={getPriceLabel(entry)}
                                        color="primary"
                                        sx={{ fontWeight: 800 }}
                                    />
                                </Box>
                            </ListItemButton>
                        );
                    })}
                </Box>
            ))}
        </Box>
    );

    return (
        <div className={"CategoryView__Main"}>
            {isRetailPos && viewMode === "list" && !isDragDropMode ? (
                renderListView()
            ) : isDragDropMode ? (
                <SortableList items={filteredItems} onSortEnd={onSortEnd} onClick={onClick} axis="xy" isDraggable={isDragDropMode} />
            ) : (
                <Grid
                    container
                    spacing={3}
                    style={{ 
                        width: '100%', 
                        boxSizing: 'border-box',
                        minHeight: 'auto', // Let content determine height
                        height: 'auto', // Allow natural growth
                        paddingBottom: '40px' // Increased bottom padding to ensure content height
                    }}
                >
                    {filteredItems &&
                        filteredItems.length > 0 &&
                        filteredItems.map((entry) => (
                            <Grid item xs={6} key={entry.item_code}>
                                <PosItemCard
                                    promotionLabel={promotionItems[entry.item_code]}
                                    item={entry}
                                    onClick={onClick}
                                    isDragDropMode={isDragDropMode}
                                    showQtyBadge={isRetailPos}
                                    showPriceTag={isRetailPos}
                                />
                            </Grid>
                        ))}
                </Grid>
            )}
        </div>
    );
}
